import { computed, Injectable, Signal, signal } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { fromEvent } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';

export type BreakpointAlias =
  | 'phone'
  | 'mobile'
  | 'handset'
  | 'tablet'
  | 'tabletDown'
  | 'laptop'
  | 'desktop';

export type ViewMode = 'auto' | 'mobile' | 'desktop';

export const BREAKPOINTS: Record<BreakpointAlias, string> = {
  // Phone-sized screens — matches $bp-dialog-fullscreen (578px)
  phone: '(max-width: 578px)',
  // Small phones
  handset: '(max-width: 600px)',
  // Phones (portrait/landscape)
  mobile: '(max-width: 1024px)',
  // Tablets (portrait/landscape)
  tablet: '(min-width: 769px) and (max-width: 1024px)',
  // Tablet and below
  tabletDown: '(max-width: 1024px)',
  // Small laptops
  laptop: '(min-width: 1025px) and (max-width: 1440px)',
  // Desktops
  desktop: '(min-width: 1441px)',
};

@Injectable({ providedIn: 'root' })
export class ViewportService {
  private readonly _width = signal(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );

  // ── View mode override (server-driven) ──
  private readonly _viewMode = signal<ViewMode>('auto');
  public readonly viewMode: Signal<ViewMode> = this._viewMode.asReadonly();

  // ── Granular menu mode override (server-driven) ──
  private readonly _menuMode = signal<ViewMode>('auto');
  public readonly menuMode: Signal<ViewMode> = this._menuMode.asReadonly();

  // ── Raw breakpoint signals ──
  private readonly _bpPhone: Signal<boolean>;
  private readonly _bpHandset: Signal<boolean>;
  private readonly _bpMobile: Signal<boolean>;
  private readonly _bpTablet: Signal<boolean>;
  private readonly _bpTabletDown: Signal<boolean>;
  private readonly _bpLaptop: Signal<boolean>;
  private readonly _bpDesktop: Signal<boolean>;

  // ── Public breakpoint signals (respect viewMode override) ──
  public readonly isPhone: Signal<boolean>;
  public readonly isHandset: Signal<boolean>;
  public readonly isMobile: Signal<boolean>;
  public readonly isTablet: Signal<boolean>;
  public readonly isTabletDown: Signal<boolean>;
  public readonly isLaptop: Signal<boolean>;
  public readonly isDesktop: Signal<boolean>;
  public readonly isElectronApp: Signal<boolean>;
  public readonly isTouchDevice: Signal<boolean>;

  public readonly width: Signal<number> = this._width.asReadonly();

  /**
   * Returns real width in 'auto' mode, or a synthetic value
   * (0 for 'mobile', 9999 for 'desktop') so width-based
   * responsive logic follows the override.
   * Note: tables always use real viewport width and are not affected.
   */
  public readonly effectiveWidth: Signal<number> = computed(() => {
    const mode = this._viewMode();
    if (mode === 'mobile') return 0;
    if (mode === 'desktop') return 9999;
    return this._width();
  });

  /**
   * Whether menus should render as mobile bottom sheets.
   * Uses 768px threshold in 'auto' mode (phone-only, not tablet).
   */
  public readonly isMenuMobile: Signal<boolean> = computed(() => {
    const mode = this._menuMode();
    if (mode === 'mobile') return true;
    if (mode === 'desktop') return false;
    // 'auto' menu mode follows an explicit view mode, falling back to width.
    const viewMode = this._viewMode();
    if (viewMode === 'mobile') return true;
    if (viewMode === 'desktop') return false;
    return this._width() <= 768;
  });

  constructor(private readonly breakpointObserver: BreakpointObserver) {
    if (typeof window !== 'undefined') {
      this._width.set(window.innerWidth);

      fromEvent(window, 'resize')
        .pipe(debounceTime(16))
        .subscribe(() => {
          this._width.set(window.innerWidth);
        });
    }

    this._bpPhone = this.observeBreakpoint(BREAKPOINTS.phone);
    this._bpHandset = this.observeBreakpoint(BREAKPOINTS.handset);
    this._bpMobile = this.observeBreakpoint(BREAKPOINTS.mobile);
    this._bpTablet = this.observeBreakpoint(BREAKPOINTS.tablet);
    this._bpTabletDown = this.observeBreakpoint(BREAKPOINTS.tabletDown);
    this._bpLaptop = this.observeBreakpoint(BREAKPOINTS.laptop);
    this._bpDesktop = this.observeBreakpoint(BREAKPOINTS.desktop);

    const mode = this._viewMode;
    this.isPhone = computed(() =>
      mode() === 'mobile'
        ? true
        : mode() === 'desktop'
          ? false
          : this._bpPhone(),
    );
    this.isHandset = computed(() =>
      mode() === 'mobile'
        ? true
        : mode() === 'desktop'
          ? false
          : this._bpHandset(),
    );
    this.isMobile = computed(() =>
      mode() === 'mobile'
        ? true
        : mode() === 'desktop'
          ? false
          : this._bpMobile(),
    );
    this.isTablet = computed(() =>
      mode() === 'mobile'
        ? false
        : mode() === 'desktop'
          ? false
          : this._bpTablet(),
    );
    this.isTabletDown = computed(() =>
      mode() === 'mobile'
        ? true
        : mode() === 'desktop'
          ? false
          : this._bpTabletDown(),
    );
    this.isLaptop = computed(() =>
      mode() === 'mobile'
        ? false
        : mode() === 'desktop'
          ? false
          : this._bpLaptop(),
    );
    this.isDesktop = computed(() =>
      mode() === 'mobile'
        ? false
        : mode() === 'desktop'
          ? true
          : this._bpDesktop(),
    );
    this.isElectronApp = signal(this.isElectronPlatform()).asReadonly();
    this.isTouchDevice = this.observeBreakpoint('(pointer: coarse)');
  }

  public setViewMode(mode: ViewMode): void {
    this._viewMode.set(this.normalizeMode(mode));
  }

  public setMenuMode(mode: ViewMode): void {
    this._menuMode.set(this.normalizeMode(mode));
  }

  private normalizeMode(mode: ViewMode): ViewMode {
    return this.isNativePlatform() && mode === 'desktop' ? 'auto' : mode;
  }

  public isMobileDevice(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
      return false;
    }

    const userAgent = navigator.userAgent || '';
    const isMobileUA =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(
        userAgent.toLowerCase(),
      );

    if (!isMobileUA) {
      return false;
    }

    const hasTouchSupport =
      'ontouchstart' in window ||
      ((navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints ??
        0) > 0;

    const hasCoarsePointer =
      !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

    return hasTouchSupport || hasCoarsePointer;
  }

  public isNativePlatform(): boolean {
    try {
      return Capacitor?.isNativePlatform() ?? false;
    } catch {
      return false;
    }
  }

  public isNativeIos(): boolean {
    return this.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  }

  public isNativeAndroid(): boolean {
    return this.isNativePlatform() && Capacitor.getPlatform() === 'android';
  }

  public isElectronPlatform(): boolean {
    try {
      const win = window as unknown as Record<string, unknown>;
      return (
        typeof window !== 'undefined' &&
        win['electronAPI'] !== undefined &&
        (win['electronAPI'] as { isElectron?: boolean })?.isElectron === true
      );
    } catch {
      return false;
    }
  }

  private observeBreakpoint(query: string): Signal<boolean> {
    const obs$ = this.breakpointObserver
      .observe(query)
      .pipe(map((state: BreakpointState) => state.matches));

    return toSignal(obs$, { initialValue: false });
  }
}
